import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useFetchAuthorisation, useFetchUserData } from "../../utils/AppFunctions";
import config from "../../app-config";
import "react-toastify/dist/ReactToastify.css";
import AdminLayout from "../../layouts/AdminLayout";
import { create_new_mobile_campaign, delete_mobile_campaign, fetch_all_contact_groups, fetch_mobile_campaign_by_id, update_mobile_campaign_by_mobile_campaign_id } from "../../api/campaignsAPI";
import notify from "../../utils/IANotification";
import { MdArrowBack, MdSave, MdDelete, MdSend } from "react-icons/md";
import Switch from 'react-switch'
import Select from 'react-select'
import SubNavbar from "../../components/ui/SubNavbar";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";

export default function CampaignPushFormPage() {
  const navigate = useNavigate();
  useFetchAuthorisation("user");
  useFetchUserData();

  let campaign_id = new URLSearchParams(window.location.search).get("campaign_id");

  const [activeTab, setActiveTab] = useState("general");
  const [campaignData, setCampaignData] = useState({
    title: "",
    description: "",
    scheduled_datetime: "",
    status: "draft",
    is_everyone: 0,
    group_ids: [],
  })
  const [loading, setLoading] = useState(true)
  const [groupData, setGroupData] = useState([])

  const tabs = [
    { id: 'general', label: 'Push Message Content' },
    { id: 'schedule_target', label: 'Schedule & Recipients' },
  ]

  const fetchCampaignGroupById = async () => {
    setLoading(true)
    const { data, error, message } = await fetch_all_contact_groups()
    if (error) notify({ message: message || error, type: 'error' })
    setGroupData(data || [])
    setLoading(false)
  }

  useEffect(() => {
    fetchCampaignById()
    fetchCampaignGroupById()
  }, [])

  const fetchCampaignById = async () => {
    if (!campaign_id || campaign_id === "new") return
    setLoading(true)
    const { data, error, message } = await fetch_mobile_campaign_by_id(campaign_id)
    if (error) notify({ message: message || error, type: 'error' })
    if (data) {
      setCampaignData({
        ...data,
        group_ids: data.group_ids || []
      })
    }
    setLoading(false)
  }

  const createNewCampaign = async (options) => {
    const { data, error, message } = await create_new_mobile_campaign({ ...campaignData, ...options })
    if (error) return notify({ message: message || error, type: 'error' })
    notify({ message: "Push campaign created successfully", type: 'success' })
    navigate(`/admin/campaigns/push/details?campaign_id=${data?.id}`)
  }

  const updateCampaignById = async (options) => {
    if (!campaign_id || campaign_id === "new") return
    const { error, message } = await update_mobile_campaign_by_mobile_campaign_id(campaign_id, { ...campaignData, ...options })
    if (error) return notify({ message: message || error, type: 'error' })
    notify({ message: "Push campaign updated successfully", type: 'success' })
    fetchCampaignById()
  }

  const deleteCampaignById = async () => {
    if (!campaign_id || campaign_id === "new") return
    const { error, message } = await delete_mobile_campaign(campaign_id)
    if (error) return notify({ message: message || error, type: 'error' })
    notify({ message: "Push campaign deleted successfully", type: 'success' })
    navigate("/admin/campaigns/push")
  }

  const selectOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'scheduled', label: 'Scheduled' },
  ]

  return (
    <AdminLayout
      navSelected={"Push Notification"}
      headerText={campaign_id === "new" ? "New Push Notification Campaign" : "Edit Push Campaign"}
      toolbar={
        <>
          <div className="flex items-center gap-3">
            <Button variant="outline" icon={MdArrowBack} onClick={() => navigate('/admin/campaigns/push')}>
              Back
            </Button>
            <h1 className="text-xl font-bold text-gray-900">
              {campaign_id === "new" ? "Create Push Campaign" : campaignData.title || "Edit Push Campaign"}
            </h1>
          </div>
          <div className="flex gap-2">
            {campaign_id !== "new" && (
              <Button variant="danger" icon={MdDelete} onClick={deleteCampaignById}>
                Delete
              </Button>
            )}
            <Button
              variant="secondary"
              icon={MdSend}
              onClick={() => {
                if (campaign_id === "new") createNewCampaign({ trigger_now: 1 })
                else updateCampaignById({ trigger_now: 1 })
              }}
            >
              Send Now
            </Button>
            <Button variant="primary" icon={MdSave} onClick={() => {
              if (campaign_id === "new") createNewCampaign({ trigger_now: 0 })
              else updateCampaignById({ trigger_now: 0 })
            }}>
              {campaign_id === "new" ? "Create" : "Save Changes"}
            </Button>
          </div>
        </>
      }
    >
      <SubNavbar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* TAB 1: PUSH MESSAGE CONTENT */}
      {activeTab === "general" && (
        <Card title="Push Notification Content" subtitle="Notification headline title and message body.">
          <div className="space-y-6">
            <div className="input-container">
              <label className="input-label font-semibold">Notification Title</label>
              <input
                type="text"
                className="text-input"
                value={campaignData.title || ""}
                onChange={(e) => setCampaignData({ ...campaignData, title: e.target.value })}
                placeholder="e.g. Sunday Service Live Stream Starts in 15 Minutes!"
              />
            </div>

            <div className="input-container">
              <label className="input-label font-semibold">Push Message Body</label>
              <textarea
                className="textarea-input h-32"
                value={campaignData.description || ""}
                onChange={(e) => setCampaignData({ ...campaignData, description: e.target.value })}
                placeholder="Message body displayed on mobile devices..."
              />
            </div>
          </div>
        </Card>
      )}

      {/* TAB 2: SCHEDULE & RECIPIENTS */}
      {activeTab === "schedule_target" && (
        <Card title="Broadcast Schedule &amp; Target Groups" subtitle="Configure notification schedule and target audience groups.">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="input-container">
                <label className="input-label font-semibold">Scheduled Date &amp; Time</label>
                <input
                  type="datetime-local"
                  className="text-input"
                  value={campaignData.scheduled_datetime || ""}
                  onChange={(e) => setCampaignData({ ...campaignData, scheduled_datetime: e.target.value })}
                />
              </div>

              <div className="input-container">
                <label className="input-label font-semibold">Campaign Status</label>
                <Select
                  options={selectOptions}
                  value={selectOptions.find(option => option.value === campaignData.status)}
                  onChange={(selectedOption) => setCampaignData({ ...campaignData, status: selectedOption.value })}
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div>
                <label className="font-semibold text-sm text-gray-900">Broadcast to All Mobile Users</label>
                <p className="text-xs text-gray-500">Send to all app users or restrict to selected contact groups.</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-semibold text-gray-700">
                  {campaignData.is_everyone ? "Broadcast All" : "Selected Groups Only"}
                </span>
                <Switch
                  checked={campaignData.is_everyone === 1 || campaignData.is_everyone === true}
                  onChange={(checked) => setCampaignData({ ...campaignData, is_everyone: checked ? 1 : 0 })}
                />
              </div>
            </div>

            {!campaignData?.is_everyone && (
              <div className="input-container">
                <label className="input-label font-semibold">Target Contact Groups</label>
                <Select
                  options={groupData.map(group => ({ value: group.id, label: group.name }))}
                  isMulti
                  onChange={(selected) => setCampaignData({ ...campaignData, group_ids: (selected || []).map(s => s.value) })}
                  value={groupData.filter(group => (campaignData.group_ids || []).includes(group.id)).map(group => ({ value: group.id, label: group.name }))}
                  placeholder="Select target audience groups..."
                />
              </div>
            )}
          </div>
        </Card>
      )}
    </AdminLayout>
  )
}